import type { Messages } from '.'
import { plural } from './plural'

const who = (by: unknown) => (by ? ` (${by})` : '')
const peers = (n: unknown) => `${n} ${plural('en', Number(n), { one: 'peer', other: 'peers' })}`

export const en: Messages = {
  'lang.label': 'Interface language',
  'common.home': 'Home',
  'common.guest': 'Guest',

  'unit.mbps': '{v} MB/s',
  'unit.kbps': '{v} KB/s',
  'unit.gb': '{v} GB',
  'unit.mb': '{v} MB',

  'home.title': 'Movie night for everyone, with one pause button',
  'home.lead':
    'The movie streams from a torrent and plays for everyone in the room at once. If it has not loaded for someone yet, it pauses for everybody and shows who you are waiting for. Everyone picks their own audio track and subtitles.',
  'home.newRoom': 'New room',
  'home.name': 'Name',
  'home.namePlaceholder': 'Friday movie night',
  'home.defaultName': 'Room',
  'home.token': 'Creation token',
  'home.rememberToken': 'Remember the token on this device',
  'home.create': 'Create room',
  'home.friendsHint': 'Friends do not need the token: they join with the room link.',
  'home.errToken': 'This token does not work.',
  'home.errRate': 'Too many attempts. Try again in 10 minutes.',
  'home.errServer': 'Could not create the room: the server is unreachable.',

  'room.notFound': 'There is no such room. Check the link.',
  'room.serverDown': 'The server is unreachable.',
  'room.opening': 'Opening the room',
  'room.connecting': 'Connecting',
  'room.kicked': 'The owner removed you from the room.',
  'room.deleted': 'This room was deleted.',
  'room.gone': 'This room no longer exists.',
  'room.banned': 'You were removed from this room.',
  'room.homeAria': 'chillio, home',
  'room.reconnecting': 'Reconnecting…',
  'room.rename': 'Change name',
  'room.renamePrompt': 'Your name in the room',
  'room.copyLink': 'Room link',
  'room.invite': 'Invite',
  'room.browser': 'In browser',
  'room.browserAria': 'The room in a browser',
  'room.browserLink': 'Link for a browser',
  'room.browserLinkHint': 'Anyone who opens it joins this room from the website, without Discord.',
  'room.copy': 'Copy',
  'room.openInBrowser': 'Open in my browser',
  'room.openInBrowserHint': "Full screen without Discord's panels. You will be signed in as {name}.",
  'room.handoffFailed': 'That did not work, try again',
  'presence.paused': 'Paused',
  'presence.watching': 'Watching together',
  'discord.connecting': 'Connecting to Discord…',
  'discord.problem.not_configured': 'The Discord Activity is not set up on the server.',
  'discord.problem.denied': 'Watching together needs you to sign in with Discord.',
  'discord.problem.not_in_activity': 'Discord did not confirm you are in this activity. Launch it again from the voice channel.',
  'discord.problem.server': 'The chillio server is not answering. Try again a bit later.',
  'discord.problem.invalid_token': 'That token did not work.',
  'discord.problem.too_many': 'Too many attempts. Try again in 10 minutes.',
  'discord.activate.title': 'Connect chillio here',
  'discord.activate.text': 'This is the first time chillio starts here. Enter the room creation token — whoever runs chillio knows it. It is needed once: after that rooms start here without it.',
  'discord.activate.token': 'Room creation token',
  'discord.activate.submit': 'Connect',
  'discord.retry': 'Try again',
  'discord.fullscreenTip': 'Discord does not give activities real full screen. To watch without its panels, open the room in your browser: “In browser” → “Open in my browser”.',
  'player.codecFallback': 'This device cannot decode the video in its original quality (for example, hardware acceleration is off in Discord). Switched to the compatible 720p mode.',
  'player.quality': 'Quality',
  'player.qualityAria': 'Quality: {name}',
  'player.qualityAuto': 'Auto',
  'player.qualityAutoHint': 'original; 720p on a weak connection',
  'player.qualityAutoNow': '720p now: the connection can’t keep up with the original',
  'player.qualityOriginal': 'Original',
  'player.qualityOriginalHint': '{res} as is',
  'player.qualityUnavailable': 'this device can’t decode it: 720p instead',
  'player.qualityCompressed': 'Compressed',
  'player.qualityCompressedHint': '720p, less traffic; the server transcodes',
  'player.autoCompressed': 'The connection can’t keep up with the original: switched to 720p. Change it in the quality menu.',
  'room.copyLinkAria': 'Copy the room link',
  'room.copied': 'Copied',
  'dialog.ok': 'OK',
  'dialog.cancel': 'Cancel',
  'dialog.save': 'Save',
  'dialog.done': 'Done',
  'room.tabChat': 'Chat',
  'room.tabEpisodes': 'Episodes',
  'room.tabMovie': 'Movie',

  'join.title': 'What should we call you?',
  'join.name': 'Your name in the room',
  'join.submit': 'Join the room',
  'join.remember': 'Your name is remembered on this device.',
  'join.linkExpired': 'The sign-in link from Discord has expired. Open it from Discord again or enter a name.',

  'empty.analyzing': 'Preparing the video',
  'empty.analyzingHint': 'Downloading the file header and reading its tracks. This usually takes a few seconds.',
  'empty.failed': 'This file could not be opened',
  'empty.metadata': 'Getting the torrent file list',
  'empty.metadataHint': 'Looking for peers and downloading the torrent metadata.',
  'empty.sourceFailed': 'The torrent could not be added',
  'empty.choose': 'Choose what to watch',
  'empty.ownerChoosing': 'The owner is choosing what to watch',
  'empty.addTorrent': 'Add a torrent to start',
  'empty.noTorrent': 'The owner has not added a movie yet',
  'empty.addHint': 'Paste a magnet link or upload a .torrent file on the “{tab}” tab.',
  'empty.chooseTitle': 'Choose what to watch',
  'empty.chooseTitleHint': 'In the “{tab}” tab find a film or series, then pick a release for it.',
  'empty.noTitle': 'The owner has not chosen what to watch yet',
  'empty.chooseRelease': 'Pick a release',
  'empty.chooseReleaseHint': 'In the “{tab}” tab: from the catalog or your own magnet link or .torrent.',

  'wait.starting': 'Starting',
  'wait.loading': 'Loading the video',
  'wait.noPeers': 'Looking for sources: no peers yet',
  'wait.torrent': (v) => `Torrent: ${v.rate}, ${peers(v.n)}`,
  'wait.members': 'Waiting for the video to load',
  'wait.continue': 'Continue without waiting',
  'wait.encoding': 'The server can’t compress the video fast enough',
  'wait.encodingHint': 'The 720p compression runs slower than playback. “Original” in the quality menu or a 1080p release plays without waiting.',
  'wait.skipOnce': 'Don’t wait this time',
  'wait.skipOnceFor': 'Don’t wait for {name} this time',
  'wait.never': 'Never wait',
  'wait.neverFor': 'Never wait for {name}',
  'wait.offline': 'No connection to the server',
  'wait.offlineHint': 'Reconnecting. The video will continue together with everyone.',

  'player.play': 'Play',
  'player.askPlay': 'Ask to continue',
  'player.pause': 'Pause',
  'player.askTitle': 'Send a request to the owner',
  'player.unmute': 'Turn sound on',
  'player.mute': 'Mute',
  'player.volume': 'Volume',
  'player.seek': 'Seek',
  'player.askSeek': 'Ask to seek',
  'player.reaction': 'Reaction',
  'player.reactions': 'Reactions',
  'player.reactionOne': 'Reaction {emoji}',
  'player.audio': 'Audio',
  'player.audioAria': 'Audio: {name}',
  'player.noAudio': 'No audio',
  'player.subs': 'Subtitles',
  'player.subsAria': 'Subtitles: {name}',
  'player.subsOff': 'No subtitles',
  'player.fullscreen': 'Full screen',
  'player.exitFullscreen': 'Exit full screen',
  'player.theater': 'Theater mode: just the film',
  'player.exitTheater': 'Leave theater mode',
  'player.fill': 'Fill the screen (crops the edges)',
  'player.channels': '{n} ch',
  'player.externalFile': 'separate file',
  'player.subsImage': 'image-based, not supported yet',
  'player.forced': 'signs only',
  'player.track': 'Track {n}',
  'player.subsSettings': 'Subtitle style…',
  'player.pip': 'Picture in picture',
  'player.remaining': 'Show remaining time',
  'player.elapsed': 'Show elapsed time',
  'player.withKey': '{label} ({key})',
  'osd.volume': 'Volume {n}%',
  'osd.muted': 'Muted',
  'osd.fill': 'Fill the screen',
  'osd.fit': 'Whole picture',
  'osd.audio': 'Audio: {name}',
  'osd.subs': 'Subtitles: {name}',
  'osd.subsOff': 'Subtitles off',
  'osd.noSubs': 'This video has no subtitles',
  'osd.subsSize': 'Subtitle size {n}%',
  'osd.subsDelay': 'Subtitle delay {n} s',
  'osd.seek': '{delta} s · {time}',
  'subs.sample': 'This is how subtitles will look',
  'subs.assMode': 'ASS style',
  'subs.assOriginal': 'Original',
  'subs.assPlain': 'Plain text',
  'subs.assOriginalHint': 'Fonts and styling as the author made them. Switch to “Plain text” to change size, font and color.',
  'subs.size': 'Size',
  'subs.font': 'Font',
  'subs.font.sans': 'Regular',
  'subs.font.serif': 'Serif',
  'subs.font.mono': 'Monospace',
  'subs.font.rounded': 'Rounded',
  'subs.fontSample': 'Aa',
  'subs.color': 'Color',
  'subs.edge': 'Edge',
  'subs.edge.none': 'None',
  'subs.edge.outline': 'Outline',
  'subs.edge.shadow': 'Shadow',
  'subs.background': 'Background',
  'subs.delay': 'Delay',
  'subs.secondsUnit': 's',
  'subs.position': 'Position',
  'subs.positionHint': 'Drag the subtitles right on the video with the mouse or a finger.',
  'subs.positionReset': 'Back to bottom center',
  'subs.drag': 'Drag me',
  'subs.back': 'Back to tracks',
  'subs.delayHint': 'Late — decrease, early — increase. You can type a number, e.g. -1.5. Keys G and H.',
  'subs.seconds': '{n} s',
  'subs.earlier': '0.1 s earlier',
  'subs.later': '0.1 s later',
  'subs.zero': 'Reset',
  'subs.reset': 'Restore defaults',
  'keys.title': 'Keyboard shortcuts',
  'keys.close': 'Close',
  'keys.playback': 'Playback',
  'keys.sound': 'Sound',
  'keys.subtitles': 'Subtitles',
  'keys.view': 'View',
  'keys.play': 'Pause and resume',
  'keys.seek5': 'Back and forward 5 seconds',
  'keys.seek10': 'Back and forward 10 seconds',
  'keys.percent': 'Jump to 0–90% of the film',
  'keys.ends': 'To the start and the end',
  'keys.volume': 'Volume',
  'keys.mute': 'Mute and unmute',
  'keys.audio': 'Next audio track',
  'keys.subsToggle': 'Subtitles on and off',
  'keys.subsNext': 'Next subtitles',
  'keys.subsSize': 'Subtitle size',
  'keys.subsDelay': 'Subtitles earlier and later',
  'keys.fullscreen': 'Full screen (or double click)',
  'keys.pip': 'Picture in picture',
  'keys.fill': 'Fill the screen or show the whole picture',
  'keys.help': 'This help',

  'seats.label': 'Audience',
  'seat.away': 'away',
  'seat.waited': 'waiting, {pct}%',
  'seat.catchingUp': 'catching up',
  'seat.loading': 'loading',
  'seat.watching': 'watching',
  'seat.owner': 'Owner',
  'seat.controller': 'Controls playback',
  'seat.youStatus': 'you · {status}',
  'seat.allow': 'Allow control',
  'seat.allowHint': 'pause and seek without asking',
  'seat.revoke': 'Revoke control',
  'seat.makeOwner': 'Make owner',
  'seat.makeOwnerConfirm': 'Hand the room over to {nick}? You will keep control of playback.',
  'seat.kick': 'Remove from room',
  'seat.noWait': 'Never wait',
  'seat.noWaitHint': 'the room will not stop while they load',
  'seat.noWaitBadge': 'Not waited for',
  'seat.kickConfirm': 'Remove {nick} from the room?',

  'chat.empty': 'Messages and room events will appear here.',
  'chat.placeholder': 'Message',
  'chat.send': 'Send',

  'source.torrent': 'Torrent',
  'source.metadata': 'Getting the file list…',
  'source.change': 'Change',
  'source.noTorrent': 'The owner has not added a torrent yet.',
  'source.nothingYet': 'The owner has not chosen what to watch yet.',
  'source.stats': (v) => `${peers(v.n)}, downloading at ${v.rate}`,
  'source.noVideos': 'This torrent has no video files.',
  'source.autoplay': 'Play the next episode automatically',
  'source.magnet': 'Magnet link',
  'source.add': 'Add',
  'source.upload': 'Upload .torrent',
  'source.cancel': 'Cancel',
  'source.sectionTitle': 'Watching',
  'source.sectionRelease': 'Release',
  'source.sectionFile': 'File',
  'source.sectionEpisodes': 'Episodes · {n}',
  'source.episode': 'Episode {n}',
  'source.findReleases': 'Find a release in the catalog',
  'source.orOwn': 'or your own release',
  'source.otherTitle': 'This release was picked for another title.',
  'source.findForTitle': 'Find a release for “{title}”',


  'request.pause': 'asks to pause',
  'request.play': 'asks to continue',
  'request.seek': 'asks to seek to {time}',
  'request.accept': 'Accept',
  'request.decline': 'Decline',

  'ev.play': (v) => `Playing${who(v.by)}`,
  'ev.pause': (v) => `Paused at ${v.time}${who(v.by)}`,
  'ev.seek': (v) => `Seeked to ${v.time}${who(v.by)}`,
  'ev.buffering': (v) => (v.by ? `Waiting for the video to load for ${v.by}` : 'Loading, waiting'),
  'ev.loading': 'Waiting for the torrent to download',
  'ev.skip': (v) => `Continuing without waiting${who(v.by)}`,
  'ev.ended': 'The episode ended',
  'ev.selecting': '{by} is choosing what to watch',

  'notice.askedPause': 'Asked to pause',
  'notice.askedPlay': 'Asked to continue',
  'notice.askedSeek': 'Asked to seek to {time}',
  'notice.requestExpired': 'Nobody answered the request',
  'notice.requestAccepted': (v) => `Request accepted${who(v.by)}`,
  'notice.requestDeclined': (v) => `Request declined${who(v.by)}`,
  'notice.subtitleAdded': 'Subtitles added: “{name}” ({by})',
  'notice.subtitleFollowed': 'Subtitles found for this episode: “{name}”',
  'catalog.select': 'Choose',
  'catalog.selectHint': 'Without a release: pick one later or add your own',
  'catalog.createRoomBare': 'Create a room without a release',
  'catalog.createRoomBareHint': 'Pick a release in the room or add your own',
  'title.clear': 'Remove',
  'title.choose': 'Choose a film or series',
  'title.hint': 'Subtitles are searched for the chosen title, for your own torrent too.',
  'subsearch.open': 'Find subtitles…',
  'subsearch.title': 'Subtitles from OpenSubtitles',
  'subsearch.languages': 'Languages',
  'subsearch.season': 'Season',
  'subsearch.episode': 'Episode',
  'subsearch.search': 'Search',
  'subsearch.searching': 'Searching…',
  'subsearch.empty': 'Nothing found. Try other languages or another episode.',
  'subsearch.add': 'Add',
  'subsearch.adding': 'Downloading…',
  'subsearch.turnOn': 'Turn on',
  'subsearch.hashMatch': 'For this file',
  'subsearch.hashMatchHint': 'Made for this very file: should be in sync',
  'subsearch.hi': 'hearing impaired',
  'subsearch.machine': 'machine translated',
  'subsearch.downloads': 'downloads: {n}',
  'subsearch.byHashOnly': 'No title chosen, so only the file itself is searched. Choose a film or series in the tab next to the chat to find more.',
  'subsearch.noTarget': 'First choose a film or series in the tab next to the chat: subtitles are searched for it.',
  'subsearch.quota': 'The OpenSubtitles download limit for today is used up. Try tomorrow.',
  'subsearch.expired': 'The results are out of date: search again.',
  'subsearch.error': 'Failed: {message}',
  'subsearch.close': 'Close',
  'subsearch.note': 'Downloads are anonymous, about 20 a day for the whole server. Subtitles are kept for this file for everyone.',
  'notice.roleOwner': 'You are now the room owner',
  'notice.roleController': 'You can now control playback',
  'notice.roleViewer': 'You no longer control playback',
  'notice.forbidden': 'Only the owner or a controller can do this',
  'notice.rateLimited': 'Too fast, wait a moment',
  'notice.error': 'Error: {message}',

  'catalog.title': 'Catalog',
  'catalog.find': 'Find a movie or series',
  'catalog.open': 'Find in the catalog',
  'catalog.homeLink': 'Find a movie in the catalog',
  'catalog.searchPlaceholder': 'Movie or series title',
  'catalog.searchLabel': 'Catalog search',
  'catalog.hint': 'Start typing a title: we will suggest movies and series, then find releases for the one you pick.',
  'catalog.movie': 'Movie',
  'catalog.tv': 'Series',
  'catalog.searching': 'Searching…',
  'catalog.noResults': 'Nothing found',
  'catalog.loading': 'Loading the description…',
  'catalog.back': 'Back',
  'catalog.close': 'Close',
  'catalog.minutes': '{n} min',
  'catalog.seasonsCount': (v) => `${v.n} ${plural('en', Number(v.n), { one: 'season', other: 'seasons' })}`,
  'catalog.season': 'Season {n}',
  'catalog.seasonsRange': 'Seasons {a}–{b}',
  'catalog.allSeasons': 'All seasons',
  'catalog.seasonPick': 'Season',
  'catalog.releases': 'Releases',
  'catalog.releasesLoading': 'Searching trackers for releases…',
  'catalog.noReleases': 'No releases found. Try another season or add a torrent manually.',
  'catalog.tracks.button': 'Tracks',
  'hdr.aria': '{name}: how the video is shown',
  'hdr.on': 'Your screen shows this video in HDR: full brightness and color.',
  'hdr.screenOff': 'The video is HDR, but the screen is in SDR mode, so the browser tones the picture down. If your screen supports HDR, turn it on in the system — the video brightens right away, no reload needed.',
  'hdr.transcoded': 'This browser cannot decode the video, so you are watching a 720p SDR copy. Chrome, Edge or Safari with 10-bit HEVC support show HDR.',
  'hdr.dvOnly': 'Dolby Vision profile 5 has no HDR10 base layer: browsers show it with wrong colors (purple and green tints). Better pick another release.',
  'hdr.howWindows': 'Windows: press Win + Alt + B or turn on “Use HDR” in Settings → System → Display.',
  'hdr.howMac': 'A Mac turns HDR on by itself on screens that support it (MacBook Pro with XDR, Pro Display XDR and others). This screen does not.',
  'hdr.howOther': 'Turn on HDR in the system display settings if your screen supports it.',
  'hdr.hintTitle': 'This film is HDR, and HDR is off on your screen.',
  'hdr.dismiss': 'Do not show again',
  'catalog.tracks.more': 'Details',
  'catalog.tracks.less': 'Hide',
  'catalog.tracks.loading': 'Loading tracks',
  'catalog.tracks.unknown': 'Tracks unknown',
  'catalog.tracks.fileShort': 'from file',
  'catalog.tracks.descriptionShort': 'from description',
  'catalog.tracks.fileHint': 'Checked in the file itself',
  'catalog.tracks.descriptionHint': 'As the uploader says; the file is still being checked',
  'catalog.tracks.fromFile': 'From the file',
  'catalog.tracks.episode': 'episode 1 of {n}',
  'catalog.tracks.fromDescription': 'From the release description',
  'catalog.tracks.probing': 'Reading the file from peers…',
  'catalog.tracks.probeFailed': 'Could not read the file: {error}',
  'catalog.tracks.timeout': 'peers did not send the start of the file within a minute',
  'catalog.tracks.notInDescription': 'The release description does not list tracks.',
  'catalog.tracks.translation': 'Translation',
  'catalog.tracks.audio': 'Audio',
  'catalog.tracks.subtitles': 'Subtitles',
  'catalog.tracks.none': 'none',
  'catalog.tracks.external': 'separate file',
  'catalog.tracks.forced': 'forced',
  'catalog.tracks.unsupported': 'not supported by the player',
  'catalog.sourceError.blocked': '{provider} does not answer this server: the site shows a Cloudflare check. Use another network address or a proxy (HTTPS_PROXY).',
  'catalog.sourceError.login': '{provider}: could not sign in, check the user name and password.',
  'catalog.sourceError.captcha': '{provider} asks for a captcha on sign-in: sign in from a browser and set the session cookie in the settings.',
  'catalog.peersTitle': 'Seeders: {s}, leechers: {l}',
  'catalog.openTopic': 'Open the release on the tracker',
  'catalog.disc': 'Disc image',
  'catalog.episodes': 'Episodes {e}',
  'catalog.watch': 'Watch',
  'catalog.createRoom': 'Create room',
  'catalog.adding': 'Downloading the .torrent…',
  'catalog.tokenNeeded': 'The catalog is available with the room creation token.',
  'catalog.continue': 'Continue',
  'catalog.unavailable': 'The catalog is not configured on the server.',
}
